// @ts-check
// Resumo de cada nó do fluxo para o canvas, o mini-canvas e o modo demonstração (spec 2.0.0, fluxos.md seção 20.5;
// contrato 09 de fluxos, seção 17). Função pura: não traduz nem consulta a API. Devolve o título e uma lista de partes
// (chave de texto com parâmetros ou texto pronto, com destaque e link opcionais); quem desenha traduz as chaves com o
// catálogo `flows` e junta as partes. Os nomes que dependem de dados (modelo de e-mail, etapa, campo, membro, fluxo)
// chegam pelas funções de consulta; sem elas, o resumo cai no texto genérico do tipo.
import { catalogItemForNode, flowNodeCategory, flowNodeIcon } from './flow-catalog.js';
import { isAudienceDefinitionEmpty } from './flow-definition.js';

/**
 * @typedef {import('./flow-definition.js').FlowNode} FlowNode
 * @typedef {{ text?: string, key?: string, params?: Record<string, string | number>, strong?: boolean,
 *   link?: { kind: 'EMAIL_TEMPLATE', id: string } | null, attach?: boolean }} FlowSummaryPart
 *   key: chave de texto do catálogo (com `params`); text: texto já pronto (nome de modelo, etapa, etiqueta);
 *   attach: cola na parte anterior, sem espaço (a vírgula de "2 dias, 3 horas e 5 minutos")
 * @typedef {{ titleKey: string, name: string | null, category: string | null, icon: string, parts: FlowSummaryPart[],
 *   isUnconfigured: boolean, badge: { key: string, tone: 'marketing' | 'service' } | null }} FlowNodeSummary
 *   name: o nome dado ao passo (vence o título do tipo); isUnconfigured: o resumo é o chamado "Selecione ..."
 * @typedef {{
 *   emailTemplate?: (id: string) => { name: string, purpose?: string | null } | null,
 *   whatsappTemplate?: (id: string) => { name: string, category?: string | null } | null,
 *   stageLabel?: (value: string) => string | null,
 *   optionLabel?: (fieldName: string, value: string) => string | null,
 *   fieldLabel?: (fieldName: string) => string | null,
 *   memberName?: (id: string) => string | null,
 *   flowName?: (id: string) => string | null,
 *   emailNodeLabel?: (nodeId: string) => string | null,
 *   audiencePhrases?: (audience: Record<string, any>) => string[],
 *   formatDateTime?: (instant: string) => { date: string, time: string } | null,
 * }} FlowSummaryLookups
 */

const MAXIMUM_AUDIENCE_PHRASES = 2;

/** @param {string} type @param {string} suffix */
const nodeKey = (type, suffix) => `flows.nodes.${type}.${suffix}`;

/** @param {string} key @param {Record<string, string | number>} [params] @returns {FlowSummaryPart} */
const keyPart = (key, params) => (params ? { key, params } : { key });

/** @param {string} text @param {boolean} [strong] @returns {FlowSummaryPart} */
const textPart = (text, strong = false) => (strong ? { text, strong: true } : { text });

/** @param {unknown} value */
const wholeNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : 0);

/** Nome do passo como foi dado, sem espaços sobrando. @param {FlowNode} node */
const givenName = (node) => (typeof node.name === 'string' && node.name.trim() !== '' ? node.name.trim() : null);

/**
 * Título do nó pelo tipo (ou pela estratégia, no responsável em rodízio).
 * @param {{ type: string, config?: Record<string, any> | null }} node
 */
export function flowNodeTitleKey(node) {
  return catalogItemForNode(node)?.nameKey ?? 'flows.nodes.UNKNOWN.name';
}

/**
 * Texto de uma lista de nomes, com os que faltam como "(removido)".
 * @param {string[]} ids @param {((id: string) => string | null) | undefined} lookup
 * @returns {FlowSummaryPart[]}
 */
function namesOf(ids, lookup) {
  return ids.map((id, position) => {
    const name = lookup?.(id) ?? null;
    /** @type {FlowSummaryPart} */
    const part = name ? textPart(name, true) : keyPart('flows.summary.missing');
    return position < ids.length - 1 && name ? { ...part, text: `${name},` } : part;
  });
}

/**
 * Resumo de um nó: título, partes do texto, selo da finalidade e se ainda falta configurar.
 * @param {FlowNode} node @param {FlowSummaryLookups} [lookups]
 * @returns {FlowNodeSummary}
 */
export function summarizeFlowNode(node, lookups = {}) {
  const config = node.config ?? {};
  /** @type {FlowNodeSummary} */
  const summary = {
    titleKey: flowNodeTitleKey(node),
    name: givenName(node),
    category: flowNodeCategory(node.type),
    icon: flowNodeIcon(node.type, config),
    parts: [],
    isUnconfigured: false,
    badge: null,
  };
  const unconfigured = (/** @type {string} */ key = nodeKey(node.type, 'unconfigured')) => {
    summary.parts = [keyPart(key)];
    summary.isUnconfigured = true;
    return summary;
  };
  switch (node.type) {
    case 'SEND_EMAIL': {
      if (!config.templateId) return unconfigured();
      const template = lookups.emailTemplate?.(String(config.templateId)) ?? null;
      if (!template) {
        summary.parts = [keyPart('flows.summary.templateMissing')];
        return summary;
      }
      summary.parts = [{ text: template.name, link: { kind: 'EMAIL_TEMPLATE', id: String(config.templateId) } }];
      summary.badge = template.purpose === 'SERVICE'
        ? { key: 'flows.summary.badges.service', tone: 'service' }
        : { key: 'flows.summary.badges.marketing', tone: 'marketing' };
      return summary;
    }
    case 'SEND_WHATSAPP': {
      if (!config.templateId || !config.channelId) return unconfigured();
      const template = lookups.whatsappTemplate?.(String(config.templateId)) ?? null;
      if (!template) {
        summary.parts = [keyPart('flows.summary.templateMissing')];
        return summary;
      }
      summary.parts = [textPart(template.name)];
      summary.badge = template.category === 'MARKETING'
        ? { key: 'flows.summary.badges.marketing', tone: 'marketing' }
        : { key: 'flows.summary.badges.announcement', tone: 'service' };
      return summary;
    }
    case 'WAIT':
      summary.parts = durationParts(wholeNumber(config.days), wholeNumber(config.hours), wholeNumber(config.minutes));
      return summary;
    case 'WAIT_WINDOW': {
      const days = wholeNumber(config.days);
      summary.parts = [{
        key: nodeKey('WAIT_WINDOW', 'summary'),
        params: { count: days, days, start: String(config.windowStart ?? ''), end: String(config.windowEnd ?? '') },
        strong: true,
      }];
      return summary;
    }
    case 'WAIT_UNTIL_DATE': {
      if (config.mode === 'FIELD') {
        if (!config.fieldName) return unconfigured();
        const field = lookups.fieldLabel?.(String(config.fieldName)) ?? String(config.fieldName);
        const amount = wholeNumber(config.offsetAmount);
        if (amount === 0) {
          summary.parts = [{ key: 'flows.summary.atField', params: { field }, strong: true }];
          return summary;
        }
        const unit = ['MINUTES', 'HOURS', 'DAYS'].includes(config.offsetUnit) ? config.offsetUnit : 'HOURS';
        summary.parts = [
          { key: `flows.summary.offset.${unit}`, params: { count: Math.abs(amount) }, strong: true },
          { key: amount < 0 ? 'flows.summary.direction.BEFORE' : 'flows.summary.direction.AFTER', strong: true },
          textPart(field, true),
        ];
        return summary;
      }
      if (!config.at) return unconfigured();
      const formatted = lookups.formatDateTime?.(String(config.at)) ?? null;
      summary.parts = formatted
        ? [{ key: nodeKey('WAIT_UNTIL_DATE', 'summaryFixed'), params: { date: formatted.date, time: formatted.time }, strong: true }]
        : [textPart(String(config.at), true)];
      return summary;
    }
    case 'CONDITION': {
      if (isAudienceDefinitionEmpty(config.audience, 'CONDITION')) return unconfigured();
      const phrases = (lookups.audiencePhrases?.(config.audience) ?? []).filter(Boolean).slice(0, MAXIMUM_AUDIENCE_PHRASES);
      summary.parts = phrases.length > 0
        ? phrases.map((phrase, position) => textPart(position < phrases.length - 1 ? `${phrase};` : phrase))
        : [keyPart('flows.summary.criteriaSet')];
      return summary;
    }
    case 'EMAIL_ENGAGEMENT': {
      if (!config.emailNodeId) return unconfigured();
      const email = lookups.emailNodeLabel?.(String(config.emailNodeId)) ?? null;
      const event = ['OPENED', 'CLICKED', 'OPENED_OR_CLICKED'].includes(config.event) ? config.event : 'OPENED_OR_CLICKED';
      summary.parts = [{ key: `flows.nodes.EMAIL_ENGAGEMENT.summary.${event}`, params: { email: email ?? '' } }];
      return summary;
    }
    case 'ADD_TO_FLOW': {
      const flowIds = Array.isArray(config.flowIds) ? config.flowIds.map(String) : [];
      if (flowIds.length === 0) return unconfigured();
      summary.parts = namesOf(flowIds, lookups.flowName);
      return summary;
    }
    case 'REMOVE_FROM_FLOWS': {
      if (config.mode === 'ALL_OTHERS') {
        summary.parts = [keyPart(nodeKey('REMOVE_FROM_FLOWS', 'summaryAll'))];
        return summary;
      }
      const flowIds = Array.isArray(config.flowIds) ? config.flowIds.map(String) : [];
      if (flowIds.length === 0) return unconfigured();
      summary.parts = namesOf(flowIds, lookups.flowName);
      return summary;
    }
    case 'UPDATE_STAGE': {
      if (!config.stage) return unconfigured();
      const stage = lookups.stageLabel?.(String(config.stage)) ?? String(config.stage);
      summary.parts = [{ key: nodeKey('UPDATE_STAGE', 'summary'), params: { stage }, strong: true }];
      const reasonField = config.stage === 'LOST' ? 'lostReason' : config.stage === 'NOT_A_LEAD' ? 'notLeadReason' : null;
      const reason = reasonField && config[reasonField] ? lookups.optionLabel?.(reasonField, String(config[reasonField])) ?? String(config[reasonField]) : null;
      if (reason) summary.parts.push(textPart(`(${reason})`));
      return summary;
    }
    case 'UPDATE_FIELD': {
      if (!config.fieldName) return unconfigured();
      const field = lookups.fieldLabel?.(String(config.fieldName)) ?? String(config.fieldName);
      summary.parts = [{ key: nodeKey('UPDATE_FIELD', 'summary'), params: { field }, strong: true }];
      return summary;
    }
    case 'ADD_TAGS':
    case 'REMOVE_TAGS': {
      const values = Array.isArray(config.values) ? config.values.map(String) : [];
      if (values.length === 0) return unconfigured();
      const fieldName = String(config.fieldName ?? 'tags');
      const labels = values.map((value) => lookups.optionLabel?.(fieldName, value) ?? value);
      summary.parts = [textPart(labels.join(', '), true)];
      return summary;
    }
    case 'ASSIGN_OWNER': {
      const memberIds = Array.isArray(config.workspaceMemberIds) ? config.workspaceMemberIds.map(String) : [];
      if (config.strategy === 'ROUND_ROBIN') {
        summary.parts = memberIds.length === 0
          ? [{ key: nodeKey('ASSIGN_OWNER', 'summaryWholeTeam'), strong: true }]
          : [{ key: nodeKey('ASSIGN_OWNER', 'summaryRoundRobin'), params: { count: memberIds.length }, strong: true }];
        return summary;
      }
      if (memberIds.length === 0) return unconfigured();
      const name = lookups.memberName?.(memberIds[0]) ?? null;
      summary.parts = [name ? textPart(name, true) : keyPart('flows.summary.missing')];
      return summary;
    }
    case 'CREATE_TASK':
      if (typeof config.title !== 'string' || config.title.trim() === '') return unconfigured();
      summary.parts = [textPart(config.title.trim())];
      return summary;
    case 'SEND_NOTIFICATION': {
      if (typeof config.title !== 'string' || config.title.trim() === '') return unconfigured();
      const recipient = ['RECORD_OWNER', 'SPECIFIC', 'ALL_MEMBERS'].includes(config.recipient) ? config.recipient : 'RECORD_OWNER';
      const count = Array.isArray(config.workspaceMemberIds) ? config.workspaceMemberIds.length : 0;
      summary.parts = [{ key: `flows.nodes.SEND_NOTIFICATION.summary.${recipient}`, params: { count } }];
      return summary;
    }
    case 'CALL_WEBHOOK':
    case 'HTTP_REQUEST': {
      const host = hostOf(config.url);
      if (!host) return unconfigured();
      summary.parts = [textPart(host)];
      return summary;
    }
    default:
      summary.parts = [keyPart('flows.summary.unknownType')];
      return summary;
  }
}

/**
 * Duração só com as unidades que não são zero: "5 minutos", "2 dias e 3 horas", "1 dia, 2 horas e 30 minutos".
 * @param {number} days @param {number} hours @param {number} minutes
 * @returns {FlowSummaryPart[]}
 */
function durationParts(days, hours, minutes) {
  const units = /** @type {Array<[number, string]>} */ ([[days, 'DAYS'], [hours, 'HOURS'], [minutes, 'MINUTES']]).filter(([amount]) => amount > 0);
  if (units.length === 0) return [{ key: 'flows.summary.offset.MINUTES', params: { count: 0 }, strong: true }];
  /** @type {FlowSummaryPart[]} */
  const parts = [];
  units.forEach(([amount, unit], position) => {
    if (position > 0 && position === units.length - 1) parts.push(keyPart('flows.summary.and'));
    parts.push({ key: `flows.summary.offset.${unit}`, params: { count: amount }, strong: true });
    if (position < units.length - 2) parts.push({ text: ',', attach: true });
  });
  return parts;
}

/** Domínio de uma URL (o resto pode ter segredos e variáveis). @param {unknown} value */
function hostOf(value) {
  if (typeof value !== 'string' || value.trim() === '') return null;
  try {
    return new URL(value.trim()).hostname || null;
  } catch {
    // URL com variável no domínio: mostra o começo, sem a consulta
    return value.trim().replace(/^https?:\/\//i, '').split(/[/?#]/)[0] || null;
  }
}

/**
 * Texto corrido do resumo (dica, leitor de tela, mini-canvas), com a tradução de quem chama.
 * @param {FlowNodeSummary} summary @param {(key: string, params?: Record<string, unknown>) => string} translate
 */
export function flowSummaryText(summary, translate) {
  let text = '';
  for (const part of summary.parts) {
    const piece = part.key ? translate(part.key, part.params ?? {}) : part.text ?? '';
    if (piece === '') continue;
    text += text === '' || part.attach ? piece : ` ${piece}`;
  }
  return text;
}

/**
 * Título do nó já traduzido: o nome dado ao passo ou o nome do tipo.
 * @param {FlowNodeSummary} summary @param {(key: string, params?: Record<string, unknown>) => string} translate
 */
export const flowSummaryTitle = (summary, translate) => summary.name ?? translate(summary.titleKey);
